import { useQuery } from "@tanstack/react-query";
import { create } from "zustand";
import FlightReservationServices from "@/services/flightReservationServices";
import RefundProcessServices from "@/services/refundProcessServices";
import { FlightDelayWithRefundProcess } from "@/types/flightReservation";
import { getGlobalSessionId } from "@/services";

type FlightReservationsState = {
  reservations: FlightDelayWithRefundProcess[];
  setReservations: (_reservations: FlightDelayWithRefundProcess[]) => void;
};

const useFlightReservationsStore = create<FlightReservationsState>((set) => ({
  reservations: [],
  setReservations: (reservations) => set({ reservations }),
}));

export const useFlightReservations = () => {
  const { setReservations } = useFlightReservationsStore();
  const reservations = useFlightReservationsStore(
    (state) => state.reservations
  );

  const sessionId = getGlobalSessionId();

  const query = useQuery({
    queryKey: ["flightReservations"],
    enabled: !!sessionId, // Only run query if sessionId exists
    queryFn: async () => {
      try {
        // Fetch both flight reservations (with delay notices) and refund processes
        const [flightReservations, refundProcesses] = await Promise.all([
          FlightReservationServices.getReservations(),
          RefundProcessServices.getRefundProcesses(),
        ]);

        // Create a map of refund processes for flight reservations
        const flightRefundProcessMap = new Map();
        refundProcesses
          .filter(
            (p: any) => p.type === "FLIGHT" && p.referencePk && p.referenceSk
          )
          .forEach((process: any) => {
            const key = `${process.referencePk}#${process.referenceSk}`;
            flightRefundProcessMap.set(key, process);
          });

        // Map flight reservations with refund process data
        const reservationsWithRefund: FlightDelayWithRefundProcess[] =
          flightReservations.map((reservation) => {
            const key = `${reservation.pk}#${reservation.sk}`;
            const process = flightRefundProcessMap.get(key);

            return {
              ...reservation,
              currentStatus: process?.currentStatus || null,
              reasonForIntervention: process?.reason || null,
              refundProcessPk: process?.pk,
              refundProcessSk: process?.sk,
            };
          });

        setReservations(reservationsWithRefund);
        return reservationsWithRefund;
      } catch (error) {
        console.error("✈️ useFlightReservations - Error:", error);
        throw error;
      }
    },
  });

  return {
    reservations,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};
